import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  BarChart3,
  GitBranch,
  Columns3,
} from 'lucide-react';
import { List, MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../../icons';
import { ProjectDto } from '../../../services';
import { ProgressOverview } from './ProgressOverview';
import { TimelineView } from './TimelineView';
import { PieceKanban } from './PieceKanban';
import { PieceList } from './PieceList';
import './OpusDashboard.css';

interface OpusDashboardProps {
  opus: ProjectDto;
  pieces: ProjectDto[];
  isLoading?: boolean;
  onBack?: () => void;
  onAddPiece?: () => void;
  onPieceClick?: (piece: ProjectDto) => void;
  onEditPiece?: (piece: ProjectDto) => void;
  onArchivePiece?: (piece: ProjectDto) => void;
  onCompletePiece?: (piece: ProjectDto) => void;
  onMovePiece?: (pieceId: string, newStage: string) => void;
}

type ViewType = 'progress' | 'timeline' | 'kanban' | 'list';

const VIEW_STORAGE_KEY = 'opus-dashboard-view';

const disciplineIcons: Record<string, React.ReactNode> = {
  Music: <MusicIcon size={18} />,
  Art: <VisualArtIcon size={18} />,
  Writing: <WritingIcon size={18} />,
  Video: <VideoIcon size={18} />,
};

export const OpusDashboard: React.FC<OpusDashboardProps> = ({
  opus,
  pieces,
  isLoading = false,
  onBack,
  onAddPiece,
  onPieceClick,
  onEditPiece,
  onArchivePiece,
  onCompletePiece,
  onMovePiece,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get initial view from URL or localStorage
  const getInitialView = (): ViewType => {
    const urlView = searchParams.get('view') as ViewType;
    if (urlView && ['progress', 'timeline', 'kanban', 'list'].includes(urlView)) {
      return urlView;
    }
    const storedView = localStorage.getItem(VIEW_STORAGE_KEY) as ViewType;
    if (storedView && ['progress', 'timeline', 'kanban', 'list'].includes(storedView)) {
      return storedView;
    }
    return 'progress';
  };

  const [currentView, setCurrentView] = useState<ViewType>(getInitialView);

  // Update URL and localStorage when view changes
  const handleViewChange = useCallback((view: ViewType) => {
    setCurrentView(view);
    setSearchParams({ view });
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [setSearchParams]);

  // Sync view from URL on initial load and URL changes
  useEffect(() => {
    const urlView = searchParams.get('view') as ViewType;
    if (urlView && ['progress', 'timeline', 'kanban', 'list'].includes(urlView)) {
      setCurrentView(urlView);
      localStorage.setItem(VIEW_STORAGE_KEY, urlView);
    }
  }, [searchParams]);

  // Redirect if not an Opus (is a Piece)
  useEffect(() => {
    if (opus.category === 'Piece' && !isLoading) {
      navigate(`/projects/${opus.id}`, { replace: true });
    }
  }, [opus.category, opus.id, isLoading, navigate]);

  const completedPieces = pieces.filter(p => p.status === 'Completed').length;
  const totalPieces = pieces.length;

  const views: { id: ViewType; label: string; icon: React.ReactNode }[] = [
    { id: 'progress', label: t('opusDashboard.progress', 'Progress'), icon: <BarChart3 size={16} /> },
    { id: 'timeline', label: t('opusDashboard.timeline', 'Timeline'), icon: <GitBranch size={16} /> },
    { id: 'kanban', label: t('opusDashboard.kanban', 'Kanban'), icon: <Columns3 size={16} /> },
    { id: 'list', label: t('opusDashboard.list', 'List'), icon: <List size={16} /> },
  ];

  const renderView = () => {
    switch (currentView) {
      case 'progress':
        return (
          <ProgressOverview
            opus={opus}
            pieces={pieces}
            isLoading={isLoading}
            onAddPiece={onAddPiece}
            onViewAllPieces={() => handleViewChange('list')}
            onPieceClick={onPieceClick}
          />
        );
      case 'timeline':
        return (
          <TimelineView
            pieces={pieces}
            isLoading={isLoading}
            onPieceClick={onPieceClick}
          />
        );
      case 'kanban':
        return (
          <PieceKanban
            pieces={pieces}
            isLoading={isLoading}
            onPieceClick={onPieceClick}
            onMovePiece={onMovePiece}
            onAddPiece={onAddPiece}
          />
        );
      case 'list':
        return (
          <PieceList
            pieces={pieces}
            isLoading={isLoading}
            onPieceClick={onPieceClick}
            onEditPiece={onEditPiece}
            onArchivePiece={onArchivePiece}
            onCompletePiece={onCompletePiece}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="opus-dashboard">
      {/* Back Navigation */}
      <div className="opus-dashboard__back">
        <button
          className="opus-dashboard__back-btn"
          onClick={onBack}
          aria-label={t('opusDashboard.backToProjects', 'Back to Projects')}
        >
          <ArrowLeft size={20} />
          <span>{t('opusDashboard.projects', 'Projects')}</span>
        </button>
      </div>

      {/* Opus Header */}
      <header className="opus-dashboard__header">
        <div className="opus-dashboard__header-main">
          <div className="opus-dashboard__discipline">
            {disciplineIcons[opus.projectType] || <MusicIcon size={18} />}
            <span>{opus.projectType}</span>
          </div>
          <h1 className="opus-dashboard__title">{opus.title}</h1>
          {opus.description && (
            <p className="opus-dashboard__description">{opus.description}</p>
          )}
        </div>
        <div className="opus-dashboard__header-stats">
          <div className="opus-dashboard__progress-stat">
            <span className="opus-dashboard__progress-count">
              {completedPieces}/{totalPieces}
            </span>
            <span className="opus-dashboard__progress-label">
              {t('opusDashboard.piecesCompleted', 'Pieces Completed')}
            </span>
          </div>
        </div>
      </header>

      {/* Action Bar */}
      <div className="opus-dashboard__actions">
        {/* View Switcher */}
        <nav className="opus-dashboard__view-switcher" role="tablist" aria-label={t('opusDashboard.viewOptions', 'View options')}>
          {views.map((view) => (
            <button
              key={view.id}
              role="tab"
              aria-selected={currentView === view.id}
              className={`opus-dashboard__view-tab ${currentView === view.id ? 'opus-dashboard__view-tab--active' : ''}`}
              onClick={() => handleViewChange(view.id)}
            >
              {view.icon}
              <span>{view.label}</span>
            </button>
          ))}
        </nav>

        {/* Add Piece Button */}
        <button
          className="opus-dashboard__add-btn"
          onClick={onAddPiece}
          aria-label={t('opusDashboard.addPiece', 'Add Piece')}
        >
          <Plus size={18} />
          <span>{t('opusDashboard.addPiece', 'Add Piece')}</span>
        </button>
      </div>

      {/* View Container */}
      <main className="opus-dashboard__content" role="tabpanel">
        {renderView()}
      </main>
    </div>
  );
};

export default OpusDashboard;
